import { CircularProgress } from "@mui/material";
import { singleTypeListQuery } from "api/graphqlTypes";
import { Button } from "components/Button/Button";
import { Icon } from "components/Icon/Icon";
import { IconCollection } from "components/Icon/IconCollection";
import { iconWithTooltipClassName } from "components/ObjectTemplateCanvas/components/components";
import { ObjectTemplateNode } from "components/ObjectTemplateCanvas/ObjectTemplateNode";
import { BlockingNavigationModal } from "components/Prompt/BlockingNavigationModal";
import Tooltip from "components/Tooltip/Tooltip";
import { FeConfigType } from "config/config";
import objectTemplateConfig from "config/resources/object_template";
import { Direction } from "containers/Resources/screens/ResourcesSingle/components/Visualization/visualization.service";
import { newRecordId } from "containers/Resources/screens/ResourcesSingle/utils";
import { FormApi } from "final-form";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router";
import ReactFlow, {
    ControlButton,
    Controls,
    ReactFlowInstance,
    ReactFlowProvider,
    useEdgesState,
    useNodesState,
} from "reactflow";
import "reactflow/dist/style.css";
import { actions as dataStoreAction } from "utils/DataStore/DataStore.reducer";
import strings from "utils/strings";
import { getIri } from "utils/utils";
import { ObjectTemplateCard } from "./ObjectTemplateCard";
import styles from "./ObjectTemplateCard.module.scss";
import { getPositionedGraphList, objectTemplateNodeType, objectTemplateReadOnlyNodeType } from "./utils";
import { ObjectTemplateReadOnlyNode } from "./ObjectTemplateReadOnlyNode";
import {
    isPrefilteredScope,
    resolveGraphFilter,
} from "./relationGraphFilter";
import { fetchObjectTemplateGraphList, fetchTemplateObjectCounts } from "./relationGraphFetch";
import embedStyles from "./RelationGraphEmbed.module.scss";

export interface NestedFormsDataProps {
    [key: string]: FormApi;
}

export const noDragClassName = "no-drag";

// Each template eagerly loads nested attributes + reference edges per query, so
// the GraphQL cost is (bodyCost + 1) * TEMPLATES_PER_BATCH. A batch of 20 put
// this query at exactly the 2000 GRAPHQL_QUERY_COMPLEXITY_LIMIT (zero margin);
// 10 keeps it well under while the remainder pages in progressively
// (operator-task-graphql-canvas-query-cost-limit-exceeded).
export const TEMPLATES_PER_BATCH = 10;
export const ATTRIBUTES_PER_BATCH = 10;

interface ObjectTemplateCanvasScreenProps {
    setDirtyScreen?: (value: boolean) => void;
    setNewObjectTemplateShown?: (value: boolean) => void;
    isNewObjectTemplateShown?: boolean;
    hasChanges?: boolean;
    location?: FeConfigType;
    locationId?: string;
    disabledCreateButtonTooltip?: string | null;
    /** Optional GraphQL filter (dashboard widgets). Falls back to location scope. */
    filter?: unknown;
    /** Embedded/read-only mode for dashboard widgets — no create/edit chrome. */
    embedMode?: boolean;
    className?: string;
    /**
     * Deep-link focus target: id of the object_template the canvas was opened
     * from. The matching node is highlighted and the view centers on it once it
     * loads. (Symmetric to CustomObjectCanvas focusId; node.id === template id.)
     */
    focusId?: string;
}

/**
 * ObjectTemplateCanvas component that renders a ReactFlow-based visualization
 * of object templates and their relationships.
 *
 * This component handles:
 * - Fetching and displaying object template data
 * - Managing nested forms for template editing
 * - Node positioning and layout direction
 * - Interactive node selection and form state
 * - Navigation blocking for unsaved changes
 * - Creation of new object templates
 *
 * @param setDirtyScreen - Function to set screen dirty state
 * @param setNewObjectTemplateShown - Function to control new template form visibility
 * @param isNewObjectTemplateShown - Whether new template form is shown
 * @param hasChanges - Whether there are unsaved changes
 * @param location - The type of location (project or bundle)
 * @param locationId - The unique identifier for the location
 * @param disabledCreateButtonTooltip - Tooltip for disabled create button
 * @returns JSX.Element - The rendered object template canvas
 */
const ObjectTemplateCanvas = ({
    setDirtyScreen = () => {},
    setNewObjectTemplateShown = () => {},
    isNewObjectTemplateShown = false,
    hasChanges = false,
    location,
    locationId,
    disabledCreateButtonTooltip = null,
    filter,
    embedMode = false,
    className,
    focusId,
}: ObjectTemplateCanvasScreenProps) => {
    const history = useHistory();
    const dispatch = useDispatch();
    const iri = location && locationId ? getIri(location, locationId) : "embedded";
    const key = `objectTemplateList-${iri}`;
    const resolvedFilter = resolveGraphFilter(filter, location, locationId);
    const preFiltered = isPrefilteredScope(filter) || embedMode;
    const readOnly = embedMode;

    // Whether the canvas SCOPE is settled enough to fetch. A project/bundle canvas
    // opened by ?name= knows its `location` before `locationId` resolves (Canvas.tsx
    // looks the id up from the projects/bundles store, which hydrates async). If we
    // fetch during that window `resolvedFilter` is null and we pull EVERY object
    // template in the workspace — a large, several-second query (measured ~5.3s /
    // 237KB on develop) that is then immediately superseded by the correctly-scoped
    // fetch once the id resolves. Gate the fetch on a settled scope so only the
    // scoped query fires (operator-task-fe-canvas-loading-perf-followup). Embed /
    // dashboard-widget (explicit filter) and an intentional workspace-global canvas
    // are settled immediately.
    const scopeReady =
        embedMode ||
        isPrefilteredScope(filter) ||
        location === "workspace" ||
        ((location === "project" || location === "bundle") && !!locationId);

    /**
     * Delete the new form and hide the creation interface
     */
    const deleteNewForm = useCallback(
        () => setNewObjectTemplateShown(false),
        [setNewObjectTemplateShown]
    );

    // State for managing nested forms data across multiple template nodes
    const [nestedFormsData, setNestedFormsData] = useState<NestedFormsDataProps>({});

    // State for layout direction (vertical or horizontal)
    const [layoutDirection, setLayoutDirection] = useState<Direction>(Direction.Vertical);

    // Interactive template overview: which template (if any) is expanded into its
    // editable attributes-form. Every other template renders as a lightweight
    // read-only card, so the overview does not mount ~1 form per template (the
    // CPU/fan cost — operator-task-fe-canvas-loading-perf-followup, item C1).
    const [expandedTemplateId, setExpandedTemplateId] = useState<string | null>(null);

    // ReactFlow state management for nodes and edges
    const [nodes, setNodes] = useNodesState([]);
    const [edges, setEdges] = useEdgesState([]);

    // ReactFlow instance reference for view manipulation
    const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance | null>(null);

    // Update dirty screen state when nested forms have data
    useEffect(() => {
        setDirtyScreen(Object.keys(nestedFormsData).length > 0);
    }, [nestedFormsData, setDirtyScreen]);

    const [isLoading, setLoading] = useState(true);
    // Paged-load progress (loaded / total templates) shown under the spinner,
    // so a slow multi-page load reads as progress rather than a hang
    // (operator-task-fe-object-canvas-slow-graphql-pages).
    const [loadProgress, setLoadProgress] = useState<{ loaded: number; total: number } | null>(
        null
    );
    const [fetchError, setFetchError] = useState<string | null>(null);
    const [isLoadedInitially, setIsLoadedInitially] = useState(false);

    const [, setItems] = useState<any[]>([]);
    // Retains the last fully-loaded template list so a layout-direction flip can
    // re-run the graph layout WITHOUT re-fetching (operator-task-fe-canvas-loading-perf-followup,
    // option A). This also removes the flip-triggered re-fetch that previously
    // emptied the canvas (a fresh offset-0 fetch racing the rendered graph).
    const mergedListRef = useRef<any[]>([]);
    // Templates accumulated by the current paging loop (read synchronously when
    // the last page arrives, to start the custom-object count pass).
    const pagedItemsRef = useRef<any[]>([]);
    const fetchObjectTemplateListRef = useRef<(offset: number) => Promise<unknown>>(
        () => Promise.resolve()
    );
    // Generation token for the recursive progressive-fetch loop. It is bumped
    // whenever a new top-level fetch starts (offset 0) or the component unmounts.
    // Each loop invocation captures the generation it belongs to and stops paging
    // / writing state once it no longer matches — so an in-flight paging loop does
    // not keep fetching, re-laying-out and calling setState after the canvas is
    // navigated away from (fan-whirr), nor race a concurrently-started loop
    // (operator-task-fe-canvas-loading-perf-followup, item #4).
    const fetchGenerationRef = useRef(0);
    useEffect(
        () => () => {
            // Invalidate any in-flight paging loop on unmount.
            fetchGenerationRef.current += 1;
        },
        []
    );

    // Latest hasChanges, read by the expand/collapse callbacks WITHOUT making them
    // (or the graph rebuild) depend on it — so an in-progress edit never rebuilds the
    // graph, and collapsing/switching away from a card with UNSAVED changes is blocked
    // (the same guard the Create button uses) rather than silently discarding them.
    const hasChangesRef = useRef(hasChanges);
    hasChangesRef.current = hasChanges;

    // Expand a read-only template card into its editable form (single expansion at a
    // time). Collapse returns the whole overview to lightweight read-only cards.
    // Stable identities so they don't churn buildGraph.
    const onExpandTemplate = useCallback((id: string) => {
        if (hasChangesRef.current) {
            return;
        }
        setExpandedTemplateId((prev) => (prev === id ? prev : id));
    }, []);
    const onCollapseTemplate = useCallback(() => {
        if (hasChangesRef.current) {
            return;
        }
        setExpandedTemplateId((prev) => (prev === null ? prev : null));
    }, []);

    const buildGraph = useCallback(
        (mergedList: any[]) => {
            const [nextNodes, nextEdges] = getPositionedGraphList({
                objectTemplateList: mergedList,
                location,
                locationId,
                preFiltered,
                layoutDirection,
                additionalData: {
                    fetchObjectTemplateList: () => fetchObjectTemplateListRef.current(0),
                    setNestedFormsData,
                    nestedFormsData,
                    deleteNewForm,
                    formStructure: objectTemplateConfig.form,
                    setLoading,
                    readOnly,
                    expandedTemplateId,
                    onExpandTemplate,
                    onCollapseTemplate,
                },
            });
            setNodes(nextNodes as any);
            setEdges(nextEdges as any);
        },
        [
            location,
            locationId,
            preFiltered,
            layoutDirection,
            nestedFormsData,
            deleteNewForm,
            setNodes,
            setEdges,
            readOnly,
            expandedTemplateId,
            onExpandTemplate,
            onCollapseTemplate,
        ]
    );

    const fetchEmbeddedGraph = useCallback(async () => {
        // Start a new generation so an in-flight embed load that is superseded (or
        // whose canvas unmounts — the unmount effect bumps the ref) stops paging
        // inside relationGraphFetch and does not build the graph / write state after
        // the fact (operator-task-fe-canvas-embed-path-abort-on-unmount).
        const gen = (fetchGenerationRef.current += 1);
        setLoading(true);
        setFetchError(null);
        try {
            const mergedList = await fetchObjectTemplateGraphList(resolvedFilter, {
                shouldContinue: () => gen === fetchGenerationRef.current,
            });
            if (gen !== fetchGenerationRef.current) {
                return;
            }
            mergedListRef.current = mergedList;
            buildGraph(mergedList);
            if (!isLoadedInitially) {
                setIsLoadedInitially(true);
            }
        } catch (err: any) {
            if (gen !== fetchGenerationRef.current) {
                return;
            }
            setFetchError(
                err?.response?.data?.message ?? err?.message ?? "Failed to load relation graph."
            );
            setNodes([]);
            setEdges([]);
        } finally {
            if (gen === fetchGenerationRef.current) {
                setLoading(false);
            }
        }
    }, [resolvedFilter, buildGraph, isLoadedInitially, setNodes, setEdges]);

    // Second pass: load the custom-object count of every template and merge it
    // into the painted nodes (and the retained list, so a re-layout keeps it).
    // Patching node data in place keeps the layout and the user's viewport.
    const fillObjectCounts = useCallback(
        async (list: any[], gen: number) => {
            const ids = list.map((t) => t?.id).filter(Boolean);
            if (ids.length === 0) {
                return;
            }
            const counts = await fetchTemplateObjectCounts(ids);
            if (gen !== fetchGenerationRef.current || Object.keys(counts).length === 0) {
                return;
            }
            const withCount = (template: any) =>
                template?.id in counts
                    ? {
                          ...template,
                          custom_object_list_object_template_id: {
                              ...(template.custom_object_list_object_template_id ?? {}),
                              custom_object_count: counts[template.id],
                          },
                      }
                    : template;
            mergedListRef.current = mergedListRef.current.map(withCount);
            setNodes((currentNodes: any[]) =>
                currentNodes.map((node: any) =>
                    node?.data?.value?.id in counts
                        ? { ...node, data: { ...node.data, value: withCount(node.data.value) } }
                        : node
                )
            );
        },
        [setNodes]
    );

    const fetchObjectTemplateList = useCallback(
        (offset: number, generation?: number) => {
            if (embedMode) {
                return fetchEmbeddedGraph();
            }
            // A top-level fetch (offset 0) starts a new generation; a recursive
            // page-fetch inherits the generation of the loop it belongs to.
            const gen = generation ?? (fetchGenerationRef.current += 1);
            setLoading(true);
            if (offset === 0) {
                setLoadProgress(null);
            }
            return new Promise((resolve, reject) => {
                dispatch({
                    type: dataStoreAction.START_FETCH_DATA,
                    key,
                    params: {
                        query: singleTypeListQuery(objectTemplateConfig.resourceName),
                        variables: {
                            limit: TEMPLATES_PER_BATCH,
                            order: "+name",
                            offset,
                            filter: resolvedFilter,
                            attributeLimit: ATTRIBUTES_PER_BATCH,
                            attributeOffset: 0,
                            attributeOrder: "+sort_order,+name",
                            customObjectLimit: 0,
                            // Counting custom objects is the slow part of the
                            // load; the counts come in a second pass once the
                            // last page is in (fillObjectCounts).
                            customObjectInclude: false,
                            referenceLimit: 80,
                        },
                        allow_normal: true,
                        allow_deleted: true,
                    },
                    promise: {
                        resolve: async (response: any) => {
                            // Stale loop — the canvas unmounted or a newer fetch
                            // superseded this one: stop paging and writing state.
                            if (gen !== fetchGenerationRef.current) {
                                return;
                            }
                            const newItems =
                                response?.object_template_list?.object_template_list ?? [];
                            const count =
                                response?.object_template_list?.object_template_count ?? 0;

                            const newOffset = offset + newItems.length;
                            pagedItemsRef.current =
                                offset === 0 ? newItems : [...pagedItemsRef.current, ...newItems];
                            setLoadProgress({ loaded: newOffset, total: count });
                            let mergedListForGraph: any[] | null = null;

                            setItems((currentItems) => {
                                let mergedList;

                                if (offset === 0) {
                                    mergedList = newItems;
                                } else {
                                    mergedList = [...currentItems, ...newItems];
                                }

                                if (newOffset < count) {
                                    return mergedList;
                                }

                                mergedListForGraph = mergedList;
                                return mergedList;
                            });

                            if (mergedListForGraph) {
                                mergedListRef.current = mergedListForGraph;
                                buildGraph(mergedListForGraph);
                            }

                            if (newOffset < count) {
                                fetchObjectTemplateList(newOffset, gen);
                            } else {
                                setLoading(false);
                                fillObjectCounts(pagedItemsRef.current, gen);
                                if (!isLoadedInitially) {
                                    setIsLoadedInitially(true);
                                }
                                resolve({});
                            }
                        },
                        reject: () => {
                            if (gen !== fetchGenerationRef.current) {
                                return;
                            }
                            setLoading(false);
                            reject();
                        },
                    },
                });
            });
        },
        [
            embedMode,
            fetchEmbeddedGraph,
            dispatch,
            key,
            resolvedFilter,
            buildGraph,
            fillObjectCounts,
            isLoadedInitially,
        ]
    );

    fetchObjectTemplateListRef.current = fetchObjectTemplateList;

    // Fetch the graph data when the effective SCOPE changes — NOT on a layout
    // direction flip (that only re-runs the local layout, below) and not on an
    // incidental re-render that resolves to the same scope (e.g. the ?id=→?name=
    // URL rewrite re-rendering Canvas with an unchanged locationId). The scope key
    // is the serialized GraphQL filter, which fully determines the fetched data,
    // so guarding on it de-duplicates the redundant second load
    // (operator-task-fe-canvas-loading-perf-followup, option A).
    const lastFetchScopeRef = useRef<string | null>(null);
    useEffect(() => {
        if (!(embedMode || objectTemplateConfig.form?.length)) {
            return;
        }
        // Do not fetch until the scope is settled — otherwise a ?name= deep-link
        // fires an unfiltered whole-workspace fetch before its id resolves (see
        // scopeReady above). Once locationId resolves this effect re-runs and fires
        // the single correctly-scoped fetch.
        if (!scopeReady) {
            return;
        }
        const scopeKey = `${embedMode ? "embed" : "scope"}|${resolvedFilter ?? ""}`;
        if (scopeKey === lastFetchScopeRef.current) {
            return;
        }
        lastFetchScopeRef.current = scopeKey;
        fetchObjectTemplateList(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [objectTemplateConfig.form, location, locationId, resolvedFilter, embedMode, scopeReady]);

    // Re-run ONLY the graph layout when the direction is flipped, reusing the
    // already-fetched template list — so a TB↔LR toggle re-lays-out in place
    // instead of re-fetching the whole graph (a re-fetch also emptied the canvas
    // when the fresh offset-0 fetch raced the render). No-op until the first load
    // has populated the list.
    //
    // A flip moves every node far from its previous position (TB↔LR). Because
    // <ReactFlow> stays mounted (no re-fetch → no remount that would re-fire the
    // `fitView` mount prop) and renders with `onlyRenderVisibleElements`, the
    // re-positioned nodes fall outside the current viewport and get culled —
    // leaving a blank canvas (especially TB→LR, which spreads nodes wide). Flag a
    // fitView to run once the re-laid-out nodes are committed to the store (below).
    const pendingFitViewRef = useRef(false);
    useEffect(() => {
        if (isLoading || mergedListRef.current.length === 0) {
            return;
        }
        pendingFitViewRef.current = true;
        buildGraph(mergedListRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [layoutDirection]);

    // Perform the deferred fitView once the re-laid-out nodes are in the store, so
    // a TB↔LR flip re-centres on the new layout instead of leaving the nodes
    // culled off-viewport (see pendingFitViewRef above). One requestAnimationFrame
    // lets ReactFlow process the new node positions before we fit. fitView reads
    // node dimensions from the store (measured on the initial render), so it fits
    // correctly even while the nodes are currently culled.
    useEffect(() => {
        if (!pendingFitViewRef.current || !reactFlowInstance || nodes.length === 0) {
            return;
        }
        pendingFitViewRef.current = false;
        const raf = requestAnimationFrame(() => {
            reactFlowInstance.fitView({ maxZoom: 1, minZoom: 0.2, duration: 300 });
        });
        return () => cancelAnimationFrame(raf);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nodes, reactFlowInstance]);

    // Re-run ONLY the graph layout when the expanded template changes: the expanded
    // node grows to the editable-card height and the previously-expanded one shrinks
    // back to a read-only card, so dagre must re-flow the ranks. Reuses the already-
    // fetched list (no re-fetch). No-op until the first load has populated the list.
    useEffect(() => {
        if (isLoading || mergedListRef.current.length === 0) {
            return;
        }
        buildGraph(mergedListRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expandedTemplateId]);

    // Define custom node types for ReactFlow
    const nodeTypes = useMemo(
        () => ({
            [objectTemplateNodeType]: ObjectTemplateNode,
            [objectTemplateReadOnlyNodeType]: ObjectTemplateReadOnlyNode,
        }),
        []
    );

    // Update nodes with latest nested forms data
    useEffect(() => {
        setNodes((nds) =>
            nds.map((node) => {
                node.data = {
                    ...node.data,
                    nestedFormsData,
                };

                return node;
            })
        );
    }, [nestedFormsData, reactFlowInstance, setNodes]);

    // Decorate the deep-link focus node with a highlight class (no-op otherwise).
    // node.id === the object_template's id (utils getNode: id = data.value.id).
    const displayNodes = useMemo(() => {
        if (!focusId) {
            return nodes;
        }
        return nodes.map((node) =>
            node.id === focusId
                ? {
                      ...node,
                      className: `${node.className ?? ""} ${
                          styles.focusedNode
                      }`.trim(),
                      data: { ...node.data, isFocused: true },
                  }
                : node
        );
    }, [nodes, focusId]);

    // Deep-link focus: once the focused template node has loaded (it may arrive
    // in a later progressive batch), center the view on it — one-shot.
    const hasFocusedRef = useRef(false);
    useEffect(() => {
        if (!reactFlowInstance || !focusId || hasFocusedRef.current) {
            return;
        }
        const focusNode = nodes.find((node) => node.id === focusId);
        if (focusNode) {
            reactFlowInstance.fitView({
                nodes: [{ id: focusId }],
                maxZoom: 1.0,
                minZoom: 0.5,
                duration: 400,
            });
            hasFocusedRef.current = true;
        }
    }, [reactFlowInstance, focusId, nodes]);

    /**
     * Create button component with proper disabled state and tooltip handling
     */
    const createButton = (
        <Button
            variation="white"
            icon={IconCollection.plus}
            onClick={() => !hasChanges && setNewObjectTemplateShown(true)}
            label="Create"
            disabled={isNewObjectTemplateShown || !!disabledCreateButtonTooltip}
            ariaLabel="createNewCanvasButton"
        />
    );

    return (
        <>
            <div
                className={`${styles.cardListContainer} ${
                    embedMode ? embedStyles.embeddedCanvasContainer : ""
                } ${className ?? ""}`}
            >
                {fetchError && (
                    <div className={styles.emptyData}>
                        <div>{fetchError}</div>
                    </div>
                )}
                {!embedMode && isNewObjectTemplateShown && (
                    <div className={styles.cardListHeader}>
                        <div style={{ display: "flex" }}>
                            <ObjectTemplateCard
                                key={newRecordId}
                                fetchObjectTemplateList={() => fetchObjectTemplateList(0)}
                                deleteNewForm={deleteNewForm}
                                setNestedFormsData={setNestedFormsData}
                                nestedFormsData={nestedFormsData}
                                formStructure={objectTemplateConfig.form}
                                setLoading={setLoading}
                            />
                        </div>
                    </div>
                )}

                {isLoading && (
                    <div className={styles.canvasLoader}>
                        <CircularProgress
                            disableShrink
                            className={`spinner ${isLoading ? "" : "hidden"}`}
                        />
                        {loadProgress && loadProgress.total > loadProgress.loaded && (
                            <div className={styles.canvasLoaderProgress}>
                                Loading object templates {loadProgress.loaded} / {loadProgress.total}
                            </div>
                        )}
                    </div>
                )}

                <div
                    className={`${styles.reactFlow} ${embedMode ? embedStyles.embeddedReactFlow : ""} ${
                        isLoading ? styles.reactFlowHidden : ""
                    }`}
                >
                    {nodes.length > 0 ? (
                        <ReactFlow
                            nodes={displayNodes}
                            edges={edges}
                            nodeTypes={nodeTypes}
                            // minZoom matches CustomObjectCanvas: a wide bundle must fit on open,
                            // and zoom-out must stay usable below reactflow's default of 0.5.
                            fitViewOptions={{ maxZoom: 1, minZoom: 0.2 }}
                            minZoom={0.2}
                            fitView={!focusId}
                            onlyRenderVisibleElements
                            onNodeClick={
                                embedMode
                                    ? undefined
                                    : (event: any, node) => {
                                          if (
                                              event?.target?.className ===
                                                  iconWithTooltipClassName ||
                                              event?.target?.dataset?.id === "datatype"
                                          ) {
                                              const newNodes = nodes.map((n) => {
                                                  if (n.id === node.id) {
                                                      return {
                                                          ...n,
                                                          selected: true,
                                                      };
                                                  }
                                                  return {
                                                      ...n,
                                                      selected: false,
                                                  };
                                              });
                                              setNodes(newNodes);
                                          }
                                      }
                            }
                            noDragClassName={noDragClassName}
                            onPaneClick={embedMode ? undefined : () => onCollapseTemplate()}
                            onInit={(reactFlowInstance) => setReactFlowInstance(reactFlowInstance)}
                        >
                            <Controls showInteractive={false}>
                                <Tooltip
                                    overlay={`Change to ${
                                        layoutDirection === Direction.Vertical
                                            ? "horizontal"
                                            : "vertical"
                                    } direction`}
                                >
                                    <ControlButton
                                        onClick={() => {
                                            setLayoutDirection((prevLayoutDirection) =>
                                                prevLayoutDirection === Direction.Vertical
                                                    ? Direction.Horizontal
                                                    : Direction.Vertical
                                            );
                                        }}
                                    >
                                        <Icon icon={IconCollection.rotateRight} />
                                    </ControlButton>
                                </Tooltip>
                            </Controls>
                        </ReactFlow>
                    ) : (
                        isLoadedInitially &&
                        !fetchError && (
                            <div className={styles.emptyData}>
                                <div>No object templates to display.</div>
                                {!embedMode &&
                                    (disabledCreateButtonTooltip ? (
                                        <Tooltip overlay={disabledCreateButtonTooltip}>
                                            {createButton}
                                        </Tooltip>
                                    ) : (
                                        createButton
                                    ))}
                            </div>
                        )
                    )}
                </div>
            </div>

            {!embedMode && (
                <BlockingNavigationModal
                    navigate={history.push}
                    shouldBlockNavigation={!!hasChanges}
                    titleText={strings.navigateAwayUnsavedPrompt}
                />
            )}
        </>
    );
};

/**
 * Wrapper component that provides ReactFlow context
 * @param props - Props to pass to ObjectTemplateCanvas
 * @returns JSX.Element - The wrapped ObjectTemplateCanvas component
 */
export const ObjectTemplateCanvasScreen = (props) => (
    <ReactFlowProvider>
        <ObjectTemplateCanvas {...props} />
    </ReactFlowProvider>
);
